import { createContext, useContext, type RefObject } from 'react';
import type { MobileView, Posture } from './layout';

/** Messenger-screen interactions shared between the list, chat, composer and panels. */
export interface ShellApi {
  openConversation(conversationId: string, options?: { messageId?: string }): void;
  view: MobileView;
  setView(view: MobileView): void;
  toggleDetail(force?: boolean): void;
  detailOpen: boolean;
  width: number;
  posture: Posture;
  /** < 600 px single-panel layout (not book posture). */
  single: boolean;
  composer: {
    replyTo: string | null;
    editing: string | null;
    setReplyTo(messageId: string | null): void;
    startEdit(messageId: string): void;
    cancel(): void;
    /** Inserts text at the caret and focuses the input. */
    insert(text: string): void;
    /** The composer registers its insert implementation. */
    registerInsert(fn: (text: string) => void): void;
    focus(): void;
    inputRef: RefObject<HTMLTextAreaElement | null>;
    /** Text before an edit started (restored on cancel). */
    editPrev: string;
  };
}

export const ShellContext = createContext<ShellApi | null>(null);

export function useShell(): ShellApi {
  const shell = useContext(ShellContext);
  if (!shell) throw new Error('useShell outside ShellContext');
  return shell;
}

/** What a message row asks of the composer: one object for the screen's life, so rows are not drawn again when the shell changes. */
export type ComposerActions = Pick<ShellApi['composer'], 'setReplyTo' | 'startEdit' | 'insert' | 'focus'>;

export const ComposerActionsContext = createContext<ComposerActions | null>(null);

export function useComposerActions(): ComposerActions {
  const actions = useContext(ComposerActionsContext);
  if (!actions) throw new Error('useComposerActions outside ComposerActionsContext');
  return actions;
}

export const isMacLike = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
